import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    ReferenceLine
} from 'recharts';
import './TimeGraph.css';

const NATIONAL_HOLIDAYS = {
    "2026-01-26": "Republic Day",
    "2026-03-25": "Holi",
    "2026-08-15": "Independence Day",
    "2026-10-02": "Gandhi Jayanti",
    "2026-11-01": "Diwali",
    "2026-12-25": "Christmas"
};

const formatTimeAxis = (decimal) => {
    if (decimal === null || decimal === undefined) return '';
    const hours = Math.floor(decimal);
    // eslint-disable-next-line no-unused-vars
    const minutes = Math.round((decimal - hours) * 60);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    return `${formattedHours} ${ampm}`;
};

const CustomYAxisTick = (props) => {
    const { x, y, payload } = props;
    const isBold = payload.value === 10 || payload.value === 19; // 10 AM or 7 PM

    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={4}
                textAnchor="end"
                fill={isBold ? "#1e3a8a" : "#64748b"}
                fontWeight={isBold ? "700" : "400"}
                fontSize={12}
            >
                {formatTimeAxis(payload.value)}
            </text>
        </g>
    );
};

const CustomBar = (props) => {
    const { fill, x, y, width, height, payload } = props;
    if (!height || height === 0) return null;

    const status = payload?.status;
    const isPending = payload?.isPending;

    let barClass = '';
    if (status === 'Active') barClass = 'blinking-bar';
    else if (isPending) barClass = 'blinking-bar-pending';

    return (
        <rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill={fill}
            rx={4}
            ry={4}
            className={barClass}
        />
    );
};

const TimeGraph = ({ entries, startDate, endDate }) => {
    // Add real-time update mechanism for active bars
    const [localTime, setLocalTime] = React.useState(new Date());

    React.useEffect(() => {
        const interval = setInterval(() => {
            setLocalTime(new Date());
        }, 5000); // Update every 5 seconds for "exponential" smooth feel
        return () => clearInterval(interval);
    }, []);

    // Helper to get YYYY-MM-DD regardless of locale/timezone
    const toISODateString = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const formatDateDDMMYYYY = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    };

    // Process data for the chart
    const processData = () => {
        if (!entries || entries.length === 0) {
            // Even if no entries, we should show the full month days
            const emptyData = [];
            let curr = new Date(startDate);
            const end = new Date(endDate);
            while (curr <= end) {
                const dateISO = toISODateString(curr);
                const holidayName = NATIONAL_HOLIDAYS[dateISO];
                const isSunday = curr.getDay() === 0;

                emptyData.push({
                    date: curr.getDate(),
                    fullDate: curr.toLocaleDateString('default', { weekday: 'short', month: 'short', day: 'numeric' }),
                    timeRange: null,
                    shiftRange: (isSunday || holidayName) ? [10, 19] : null,
                    status: (isSunday || holidayName) ? 'Special' : 'Absent',
                    isSunday,
                    holidayName
                });
                curr.setDate(curr.getDate() + 1);
            }
            return emptyData;
        }

        const data = [];
        let current = new Date(startDate);
        const end = new Date(endDate);

        // Fill in all days in the range
        while (current <= end) {
            const dayNum = current.getDate();
            const monthNum = current.getMonth();
            const yearNum = current.getFullYear();

            const entry = entries.find(e => {
                const eDate = new Date(e.date);
                return eDate.getDate() === dayNum &&
                    eDate.getMonth() === monthNum &&
                    eDate.getFullYear() === yearNum;
            });
            const dateISO = toISODateString(current);
            const isSunday = current.getDay() === 0;
            const holidayName = NATIONAL_HOLIDAYS[dateISO];

            let clockInVal = null;
            let clockOutVal = null;
            let durationString = '--';

            if (entry) {
                const login = new Date(entry.loginTime);
                clockInVal = login.getHours() + login.getMinutes() / 60;

                if (entry.logoutTime) {
                    const logout = new Date(entry.logoutTime);
                    const loginDate = new Date(entry.loginTime);
                    if (logout.toDateString() !== loginDate.toDateString()) {
                        clockOutVal = 23.98;
                    } else {
                        clockOutVal = logout.getHours() + logout.getMinutes() / 60;
                    }
                } else {
                    const entryDateString = toISODateString(entry.date);
                    const todayDateString = toISODateString(localTime);

                    if (entryDateString === todayDateString) {
                        clockOutVal = localTime.getHours() + localTime.getMinutes() / 60;
                    } else {
                        // Past entry with no logout -> Auto-closed at midnight
                        clockOutVal = 23.98;
                    }
                }

                let dur = clockOutVal - clockInVal;
                if (dur < 0) dur = 0;
                const hours = Math.floor(dur);
                const minutes = Math.round((dur - hours) * 60);
                durationString = `${hours}h ${minutes}m`;
            }

            const timeRange = (clockInVal !== null && clockOutVal !== null) ? [clockInVal, clockOutVal] : null;
            // Always show standard shift background (10 AM - 7 PM)
            const shiftRange = [10, 19];

            // Determine status: If past entry and no logoutTime, it's 'Completed' (Auto)
            const entryDateOnlyStr = entry ? toISODateString(entry.date) : '';
            const todayDateOnlyStr = toISODateString(localTime);
            const isPast = entry && entryDateOnlyStr !== todayDateOnlyStr;

            let status = 'Absent';
            if (entry) {
                if (entry.logoutTime || isPast) status = 'Completed';
                else status = 'Active';
            } else if (holidayName || isSunday) {
                status = 'Special';
            }

            data.push({
                date: current.getDate(),
                fullDate: current.toLocaleDateString('default', { weekday: 'short', month: 'short', day: 'numeric' }),
                timeRange,
                shiftRange,
                clockInRaw: clockInVal,
                clockOutRaw: clockOutVal,
                durationDisplay: durationString,
                status,
                isPending: entry?.changeRequest?.isRequested && entry?.changeRequest?.status === 'pending',
                isManual: !!entry?.isManual,
                isSunday,
                holidayName
            });

            current.setDate(current.getDate() + 1);
        }
        return data;
    };

    const data = processData();

    // Calculate minimum width to ensure bars are readable on mobile
    // Approx 35px per day + padding
    const minChartWidth = Math.max(600, data.length * 35);

    return (
        <div className="time-graph-container">
            <h3 className="time-graph-header">
                Attendance Timeline ({formatDateDDMMYYYY(startDate)} - {formatDateDDMMYYYY(endDate)})
            </h3>

            <div className="graph-scroll-wrapper">
                <div style={{ minWidth: `${minChartWidth}px`, height: '300px', minHeight: '300px' }}>
                    <ResponsiveContainer width="100%" height={300} minWidth={1} minHeight={1}>
                        <BarChart
                            data={data}
                            barGap={-20} // Overlay bars on the same day
                            margin={{
                                top: 20,
                                right: 30,
                                left: 0,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                            <XAxis
                                dataKey="date"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: '#64748b', fontSize: 12 }}
                                dy={10}
                            />
                            <YAxis
                                type="number"
                                domain={[7, 24]} // Show full day until midnight
                                ticks={[7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24]}
                                interval={0} // Force show all ticks on all devices
                                tick={<CustomYAxisTick />}
                                axisLine={false}
                                tickLine={false}
                                allowDataOverflow={true}
                            />
                            <Tooltip content={<CustomTooltip formatTime={formatTimeAxis} localTime={localTime} />} cursor={{ fill: 'rgba(241, 245, 249, 0.6)' }} />
                            <ReferenceLine y={10} stroke="#bbf7d0" strokeWidth={1} label="" />
                            <ReferenceLine y={19} stroke="#bbf7d0" strokeWidth={1} label="" />
                            <ReferenceLine y={23.98} stroke="#10b981" strokeWidth={2} label="11:59 PM" strokeDasharray="3 3" />

                            {/* Standard Shift Background Bar (10 AM - 7 PM) */}
                            <Bar
                                dataKey="shiftRange"
                                barSize={20}
                                radius={[4, 4, 4, 4]}
                            >
                                {data.map((entry, index) => {
                                    let color = '#f1f5f9'; // Default neutral background for standard work days
                                    if (entry.isSunday) color = '#fee2e2'; // Light Red
                                    else if (entry.holidayName) color = '#fef9c3'; // Light Yellow

                                    return <Cell key={`shift-${index}`} fill={color} />;
                                })}
                            </Bar>

                            {/* Actual Working Bar */}
                            <Bar
                                dataKey="timeRange"
                                barSize={20}
                                shape={<CustomBar />}
                            >
                                {data.map((entry, index) => {
                                    let fill = entry.status === 'Absent' || entry.status === 'Special' ? 'transparent' : '#10b981';

                                    return (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={fill}
                                            status={entry.status}
                                        />
                                    );
                                })}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

const CustomTooltip = ({ active, payload, label, formatTime, localTime }) => {
    if (active && payload && payload.length) {
        // Recharts might put shiftRange or timeRange in payload[0] depending on hovering
        // We find the one that has the extra data
        const dataPoint = payload[0].payload;

        // Helper specifically for tooltip details
        const formatTimeDetail = (decimal) => {
            if (decimal === null || decimal === undefined) return '--:--';
            const hours = Math.floor(decimal);
            const minutes = Math.round((decimal - hours) * 60);
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const formattedHours = hours % 12 || 12;
            return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
        };

        const hasWork = dataPoint.timeRange !== null;
        const isSunday = dataPoint.isSunday;
        const holidayName = dataPoint.holidayName;

        return (
            <div className="custom-tooltip">
                <div className="tooltip-date">
                    {dataPoint.fullDate}
                    {holidayName && <span className="holiday-label"> - {holidayName}</span>}
                    {!holidayName && isSunday && <span className="sunday-label"> - Sunday</span>}
                </div>

                {!hasWork && (isSunday || holidayName) && (
                    <div className="special-day-info">
                        Standard Shift: 10:00 AM - 7:00 PM
                    </div>
                )}

                {hasWork && (
                    <>
                        <div className="tooltip-row">
                            <span className="tooltip-label">Clock In:</span>
                            <span className="tooltip-value">{formatTimeDetail(dataPoint.clockInRaw)}</span>
                        </div>

                        {dataPoint.status !== 'Active' && (
                            <div className="tooltip-row">
                                <span className="tooltip-label">Clock Out:</span>
                                <span className="tooltip-value">{formatTimeDetail(dataPoint.clockOutRaw)}</span>
                            </div>
                        )}

                        <div className="tooltip-duration" style={{ color: '#10b981' }}>
                            {dataPoint.status === 'Active' ? "Status: Currently Working" : "Total"}: {dataPoint.durationDisplay}
                        </div>
                    </>
                )}
            </div>
        );
    }
    return null;
};

export default TimeGraph;
